import { useEffect, useRef, useState } from "react";

type Props = {
  image: string;
  maxOffsetPct?: number;
  className?: string;
};

type OrientationEventCtor = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

export function ParallaxHero({ image, maxOffsetPct = 18, className }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const targetRef = useRef(0);
  const currentRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const visibleRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const dragRef = useRef<{ active: boolean; startX: number; startOffset: number }>({
    active: false,
    startX: 0,
    startOffset: 0,
  });
  const [motionState, setMotionState] = useState<"idle" | "on" | "denied" | "unavailable">("idle");
  const [isVisible, setIsVisible] = useState(false);

  const clamp = (v: number) => Math.max(-maxOffsetPct, Math.min(maxOffsetPct, v));

  useEffect(() => {
    reducedMotionRef.current =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Intersection observer to gate rAF + orientation listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        visibleRef.current = entry?.isIntersecting ?? false;
        setIsVisible(entry?.isIntersecting ?? false);
      },
      { threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // rAF loop — only while visible, skipped for reduced motion
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (reducedMotionRef.current) {
      img.style.transform = "translateX(-50%)";
      return;
    }
    if (!isVisible) return;

    const tick = () => {
      currentRef.current += (targetRef.current - currentRef.current) * 0.12;
      img.style.transform = `translateX(calc(-50% + ${currentRef.current}%))`;
      if (visibleRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isVisible]);

  // Drag handlers (always mounted; cheap)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getX = (e: TouchEvent | MouseEvent) =>
      "touches" in e ? e.touches[0]?.clientX ?? 0 : (e as MouseEvent).clientX;

    const start = (e: TouchEvent | MouseEvent) => {
      dragRef.current = {
        active: true,
        startX: getX(e),
        startOffset: targetRef.current,
      };
    };
    const move = (e: TouchEvent | MouseEvent) => {
      if (!dragRef.current.active) return;
      const width = el.clientWidth || 1;
      const dx = getX(e) - dragRef.current.startX;
      const deltaPct = (dx / width) * (maxOffsetPct * 2);
      targetRef.current = clamp(dragRef.current.startOffset + deltaPct);
    };
    const end = () => {
      dragRef.current.active = false;
    };

    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: true });
    el.addEventListener("touchend", end);
    el.addEventListener("mousedown", start);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);

    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("mousedown", start);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
    };
  }, [maxOffsetPct]);

  // Device orientation — only when visible AND motion enabled
  useEffect(() => {
    if (motionState !== "on") return;
    if (!isVisible) return;
    const handler = (e: DeviceOrientationEvent) => {
      const gamma = e.gamma ?? 0;
      const clamped = Math.max(-45, Math.min(45, gamma));
      targetRef.current = clamp((clamped / 45) * maxOffsetPct);
    };
    window.addEventListener("deviceorientation", handler);
    return () => window.removeEventListener("deviceorientation", handler);
  }, [motionState, maxOffsetPct, isVisible]);

  const enableMotion = async () => {
    const Ctor = (typeof window !== "undefined"
      ? (window.DeviceOrientationEvent as OrientationEventCtor | undefined)
      : undefined);
    if (!Ctor) {
      setMotionState("unavailable");
      return;
    }
    if (typeof Ctor.requestPermission === "function") {
      try {
        const res = await Ctor.requestPermission();
        setMotionState(res === "granted" ? "on" : "denied");
      } catch {
        setMotionState("denied");
      }
    } else {
      setMotionState("on");
    }
  };

  const buttonLabel =
    motionState === "on"
      ? "Movimiento activo"
      : motionState === "denied"
        ? "Permiso denegado"
        : motionState === "unavailable"
          ? "No disponible"
          : "Activar movimiento";

  return (
    <section
      ref={containerRef}
      aria-label="Residences"
      className={`relative h-screen-safe w-full overflow-hidden bg-black select-none ${className ?? ""}`}
      style={{ touchAction: "pan-y" }}
    >
      <img
        ref={imgRef}
        src={image}
        alt="The Residences at Mandarin Oriental, Miami"
        draggable={false}
        className="absolute left-1/2 top-0 h-full min-w-[180%] max-w-none object-cover pointer-events-none will-change-transform"
        style={{ transform: "translateX(-50%)" }}
      />


      <button
        type="button"
        onClick={enableMotion}
        disabled={motionState === "on" || motionState === "unavailable"}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white/50 bg-black/25 px-5 py-2 text-[0.65rem] tracking-[0.32em] uppercase text-white backdrop-blur-sm transition hover:bg-white hover:text-black disabled:opacity-70 md:hidden"
      >
        {buttonLabel}
      </button>
    </section>
  );
}

export default ParallaxHero;
